import { A11yModule } from '@angular/cdk/a11y';
import { ConnectedPosition, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  forwardRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
  viewChild,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NG_VALIDATORS, ValidationErrors, Validator } from '@angular/forms';
import { HxBaseControl } from '../../base/hx-base-control';
import { createIdSignal } from '../../base/id-manager';
import { HxError, HxFormGroup } from '../form-group/form-group';
import { HxIcon } from '../icon/icon';
import { HxInput, InputChangeEvent, InputFocusEvent, WidthSize } from '../input/input';
import { HxLabel } from '../label/label';
import { DateAdapter } from './adapters/abstract-date-adapter';
import { HxCalendar } from './calendar/calendar';
import { DateFormats, HX_DATE_ADAPTER, HX_DATE_FORMATS, HX_DATE_LOCALE } from './date-picker-tokens';

/** Date selection type for single and range modes. */
export type DateSelection = Date | DateRange | null;

/** Date range structure for range selection mode. */
export type DateRange = {
  start: Date | null;
  end: Date | null;
};

/** Calendar view types. */
export type CalendarView = 'month' | 'year' | 'multi-year';

/**
 * Datepicker overlay component (calendar popup). Used with HxDatepickerInput directive on input elements.
 *
 * Supports both single date and date range selection through a unified single input field interface.
 *
 * **Single Date Mode:**
 *
 * - Users can type dates in format MM/DD/YYYY
 * - Example: "01/15/2024"
 *
 * **Date Range Mode:**
 *
 * - Users can type date ranges using en dash (–), em dash (—), or hyphen (-)
 * - Formats accepted: "MM/DD/YYYY – MM/DD/YYYY" or "MM/DD/YYYY - MM/DD/YYYY"
 * - Example: "01/15/2024 – 01/20/2024"
 * - Partial input (only start date) is supported: "01/15/2024" will set start date
 * - Display format uses en dash: "01/15/2024 – 01/20/2024"
 * - Incomplete ranges show as: "01/15/2024 – ..."
 *
 * @example
 *   ```html
 *   <!-- Single date mode -->
 *   <hx-form-group>
 *     <hx-label [description]="'Birth Date'"></hx-label>
 *     <hx-input [hxDatepicker]="picker"/>
 *     <hx-datepicker-toggle hxIconSuffix [for]="picker"></hx-datepicker-toggle>
 *     <hx-datepicker #picker></hx-datepicker>
 *   </hx-form-group>
 *
 *   <!-- Date range mode -->
 *   <hx-form-group>
 *     <label hx-label>Travel Dates</label>
 *     <hx-datepicker #picker mode="range"></hx-datepicker>
 *   </hx-form-group>
 *   ```;
 */
@Component({
  selector: 'hx-datepicker',
  standalone: true,
  imports: [A11yModule, HxFormGroup, HxInput, HxIcon, HxError, HxLabel],
  templateUrl: './date-picker.html',
  styleUrls: ['./date-picker.css', './date-picker-tokens.css', '../../styles/form-control-tokens.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  exportAs: 'hxDatepicker',
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => HxDatePicker),
      multi: true,
    },
  ],
  host: {
    '[class]': 'hostClasses()',
  },
})
export class HxDatePicker extends HxBaseControl implements OnInit, Validator {
  // =========================================================================
  // Dependency Injection
  // =========================================================================

  protected readonly dateAdapter = inject<DateAdapter<any>>(HX_DATE_ADAPTER);
  protected readonly locale = inject(HX_DATE_LOCALE);
  protected readonly formats = inject<DateFormats>(HX_DATE_FORMATS);
  private readonly overlay = inject(Overlay);
  private readonly elementRef = inject(ElementRef);

  private overlayRef?: OverlayRef;
  private calendarComponentRef?: any;

  /** Optional input ID (will auto-generate if not provided) */
  readonly idInput = input<string | undefined>(undefined, { alias: 'id' });

  /** Unique computed ID for the datepicker. */
  readonly computedId = createIdSignal(this.idInput, 'hx-datepicker-');

  /** The input element this datepicker is associated with. */
  private _datepickerInput: any;

  /** Whether the calendar is currently open. */
  readonly isOpen = signal<boolean>(false);

  /** Registers the input element with this datepicker. Called by HxDatepickerInput directive. */
  registerInput(input: any): void {
    if (this._datepickerInput && input !== this._datepickerInput) {
      throw new Error('HxDatepicker can only be registered with one input at a time');
    }
    this._datepickerInput = input;
  }

  // =========================================================================
  // Inputs
  // =========================================================================

  /**
   * Selection mode: single date or date range.
   *
   * @default 'single'
   */
  readonly mode = input<'single' | 'range'>('single');

  /**
   * Width size of the component.
   *
   * @default 'full'
   */
  readonly widthSize = input<WidthSize | 'default'>('full');

  /** Computed host classes for styling the component based on inputs. */
  protected readonly hostClasses = computed(() => {
    return `hx-datepicker-width-${this.widthSize()}`;
  });

  /**
   * Whether to show an inline label next to the date picker.
   *
   * @default false
   */
  readonly showLabel = input<boolean>(false);

  /**
   * Text for the inline label. Only displayed when showLabel is true.
   *
   * @default 'Date:'
   */
  readonly labelText = input<string>('Date:');

  /**
   * Alignment of the label relative to the date picker.
   *
   * - 'horizontal': Label and date picker side by side
   * - 'vertical': Label stacked above the date picker
   *
   * @default 'horizontal'
   */
  readonly labelAlignment = input<'horizontal' | 'vertical'>('horizontal');

  /**
   * Initial calendar view when opening.
   *
   * @default 'month'
   */
  readonly startView = input<CalendarView>('month');

  /**
   * Begin Date - Sets a fixed hard boundary for the earliest selectable date (inclusive). All dates before this
   * boundary are permanently disabled in the calendar view. This is a static boundary that doesn't change based on user
   * selection.
   *
   * @example
   *   ```ts
   *   // Restrict to dates starting from project start date
   *   beginDate = new Date('2024-01-01');
   *   ```;
   */
  readonly min = input<Date | null>(null);

  /**
   * End Date - Sets a fixed hard boundary for the latest selectable date (inclusive). All dates after this boundary are
   * permanently disabled in the calendar view. This is a static boundary that doesn't change based on user selection.
   *
   * @example
   *   ```ts
   *   // Restrict to dates up to project deadline
   *   endDate = new Date('2024-12-31');
   *   ```;
   */
  readonly max = input<Date | null>(null);

  /**
   * Restricts date selection to today or dates in the past. When enabled, all future dates are automatically disabled.
   * This is useful for birth dates, historical events, or any past-date scenarios.
   *
   * @example
   *   ```html
   *   <!-- Birth date picker - only past dates -->
   *   <hx-datepicker [restrictToPastOrToday]="true"></hx-datepicker>
   *   ```;
   *
   * @default false
   */
  readonly restrictToPastOrToday = input<boolean>(false);

  /**
   * Restricts date selection by preventing past dates (today and future only).
   *
   * Alias-style companion to existing restriction props so consuming apps can use clearer naming.
   *
   * @default false
   */
  readonly restrictPastDate = input<boolean>(false);

  /**
   * Restricts date selection to today or dates in the future. When enabled, all past dates are automatically disabled.
   * This is useful for appointments, bookings, event scheduling, or any future-date scenarios.
   *
   * @example
   *   ```html
   *   <!-- Appointment booking - only today or future dates -->
   *   <hx-datepicker [restrictToTodayOrFuture]="true"></hx-datepicker>
   *   ```;
   *
   * @default false
   */
  readonly restrictToTodayOrFuture = input<boolean>(false);

  /**
   * Restricts date selection by preventing future dates (past and today only).
   *
   * Alias-style companion to existing restriction props so consuming apps can use clearer naming.
   *
   * @default false
   */
  readonly restrictFutureDate = input<boolean>(false);

  /**
   * Restricts date selection to specific years only. When provided, only dates within the specified years are
   * selectable. All dates in other years are automatically disabled. This is useful for milestone years, specific
   * project phases, or multi-year planning.
   *
   * @example
   *   ```html
   *   <!-- Only allow dates in 2024, 2026, and 2028 -->
   *   <hx-datepicker [allowedYears]="[2024, 2026, 2028]"></hx-datepicker>
   *   ```;
   *
   * @default null
   */
  readonly allowedYears = input<number[] | null>(null);

  /**
   * Restricts date selection to current year only. When enabled, only dates within the current year (Jan 1 - Dec 31)
   * are selectable. This is useful for forms that need current year data only.
   *
   * @example
   *   ```html
   *   <!-- Only allow dates in current year -->
   *   <hx-datepicker [restrictToCurrentYear]="true"></hx-datepicker>
   *   ```;
   *
   * @default false
   */
  readonly restrictToCurrentYear = input<boolean>(false);

  /**
   * Number of years to look back from today for date selection. When provided, only dates from the last N years are
   * selectable. This is useful for historical data with recency limits.
   *
   * @example
   *   ```html
   *   <!-- Only allow dates from last 3 years -->
   *   <hx-datepicker [lookBackYears]="3"></hx-datepicker>
   *   ```;
   *
   * @default null
   */
  readonly lookBackYears = input<number | null>(null);

  /** Computed max date that respects restrictToPastOrToday, restrictToCurrentYear, lookBackYears, and explicit max. */
  protected readonly effectiveMaxDate = computed(() => {
    const today = this.dateAdapter.today();
    const candidates: Date[] = [];

    if (this.restrictToCurrentYear()) {
      const year = this.dateAdapter.getYear(today);
      candidates.push(new Date(year, 11, 31));
    }

    const lookBack = this.lookBackYears();
    if (lookBack !== null && lookBack > 0) {
      candidates.push(today);
    }

    if (this.restrictToPastOrToday()) {
      candidates.push(today);
    }

    if (this.restrictPastDate()) {
      candidates.push(this.dateAdapter.addCalendarDays(today, -1));
    }

    return candidates.reduce<Date | null>(
      (max, candidate) => (!max || this.dateAdapter.compareDate(candidate, max) < 0 ? candidate : max),
      this.max()
    );
  });

  /** Computed min date that respects restrictToTodayOrFuture, restrictToCurrentYear, lookBackYears, and explicit min. */
  protected readonly effectiveMinDate = computed(() => {
    const today = this.dateAdapter.today();
    const candidates: Date[] = [];

    if (this.restrictToCurrentYear()) {
      const year = this.dateAdapter.getYear(today);
      candidates.push(new Date(year, 0, 1));
    }

    const lookBack = this.lookBackYears();
    if (lookBack !== null && lookBack > 0) {
      const minDate = new Date(today);
      minDate.setFullYear(this.dateAdapter.getYear(today) - lookBack);
      candidates.push(minDate);
    }

    if (this.restrictToTodayOrFuture()) {
      candidates.push(today);
    }

    if (this.restrictFutureDate()) {
      candidates.push(this.dateAdapter.addCalendarDays(today, 1));
    }

    return candidates.reduce<Date | null>(
      (min, candidate) => (!min || this.dateAdapter.compareDate(candidate, min) > 0 ? candidate : min),
      this.min()
    );
  });

  /** Custom filter function for date availability. Return true to enable date, false to disable. */
  readonly dateFilter = input<((date: Date) => boolean) | null>(null);

  /**
   * Effective date filter that combines custom filter with allowedYears restriction. If allowedYears is set, it filters
   * dates to only those years. If a custom dateFilter is also provided, both conditions must be true.
   */
  protected readonly effectiveDateFilter = computed(() => {
    const customFilter = this.dateFilter();
    const years = this.allowedYears();

    // No restrictions - return custom filter as-is
    if (!years || years.length === 0) {
      return customFilter;
    }

    // Create year restriction filter
    const yearFilter = (date: Date): boolean => {
      const year = this.dateAdapter.getYear(date);
      return years.includes(year);
    };

    // Combine filters: both must pass
    if (customFilter) {
      return (date: Date): boolean => {
        return customFilter(date) && yearFilter(date);
      };
    }

    // Only year filter
    return yearFilter;
  });

  /** Date to display when calendar opens. Overrides selected date and today's date. */
  readonly startAt = input<Date | null>(null);

  /** CSS class(es) to apply to calendar overlay panel. */
  readonly panelClass = input<string | string[]>('');

  /**
   * CSS class for overlay backdrop.
   *
   * @default 'cdk-overlay-transparent-backdrop'
   */
  readonly backdropClass = input<string>('cdk-overlay-transparent-backdrop');

  /**
   * Marks the date picker as a required field. When enabled, the input field border will display in red (critical
   * color) when no date value is selected. This is a visual indicator only.
   *
   * @example
   *   ```html
   *   <!-- Shows red border until user selects a date -->
   *   <hx-datepicker [required]="true"></hx-datepicker>
   *   ```;
   *
   * @default false
   */
  readonly required = input<boolean>(false);

  /**
   * Indicates whether the associated checkbox is checked/enabled. When true and the date picker is empty, validation
   * errors will be displayed immediately. This is useful when the date picker visibility or requirement depends on a
   * checkbox state.
   *
   * Use this when you have checkboxes that enable/show date pickers and need validation to ensure a date is selected
   * when the checkbox is checked.
   *
   * @example
   *   ```html
   *   <!-- Date picker shown only when checkbox is checked -->
   *   <hx-checkbox
   *     [options]="[{label: 'Enable Date', value: 'enableDate'}]"
   *     [(selectedValues)]="selectedCheckboxes"></hx-checkbox>
   *
   *   <hx-datepicker
   *     *ngIf="selectedCheckboxes.includes('enableDate')"
   *     [checkboxEnabled]="selectedCheckboxes.includes('enableDate')"
   *     [requiredErrorMessage]="'Please select a date'"></hx-datepicker>
   *   ```;
   *
   * @default false
   */
  readonly checkboxEnabled = input<boolean>(false);

  /**
   * Custom error message for required validation. If not provided, defaults to 'This field is required'.
   *
   * @example
   *   ```html
   *   <hx-datepicker [required]="true" [requiredErrorMessage]="'Please select a date'"></hx-datepicker>
   *   ```;
   *
   * @default ''
   */
  readonly requiredErrorMessage = input<string>('');

  /**
   * Custom display format for the date. If not provided, uses the default format from HX_DATE_FORMATS injection token.
   *
   * Supported format tokens:
   *
   * - YYYY: 4-digit year (2024)
   * - YY: 2-digit year (24)
   * - MM: 2-digit month (01-12)
   * - M: 1 or 2-digit month (1-12)
   * - DD: 2-digit day (01-31)
   * - D: 1 or 2-digit day (1-31)
   * - MMM: Short month name (Jan, Feb)
   * - MMMM: Full month name (January, February)
   *
   * @example
   *   ```html
   *   <!-- ISO format -->
   *   <hx-datepicker [displayFormat]="'YYYY-MM-DD'"></hx-datepicker>
   *
   *   <!-- US format -->
   *   <hx-datepicker [displayFormat]="'MM/DD/YYYY'"></hx-datepicker>
   *
   *   <!-- European format -->
   *   <hx-datepicker [displayFormat]="'DD/MM/YYYY'"></hx-datepicker>
   *
   *   <!-- Month and Year only -->
   *   <hx-datepicker [displayFormat]="'MM/YYYY'"></hx-datepicker>
   *
   *   <!-- Long format -->
   *   <hx-datepicker [displayFormat]="'MMMM D, YYYY'"></hx-datepicker>
   *   ```;
   *
   * @default undefined (uses injected HX_DATE_FORMATS.display)
   */
  readonly displayFormat = input<string | undefined>(undefined);

  // =========================================================================
  // Outputs
  // =========================================================================

  /**
   * Emits when date selection commits (on blur/Enter/selection). Value type depends on mode: Date | null for single,
   * DateRange for range.
   */
  readonly dateChange = output<DateSelection>();

  /** Emits on each input change (before commit). */
  readonly dateInput = output<DateSelection>();

  /** Emits when calendar overlay opens. */
  readonly opened = output<void>();

  /** Emits when calendar overlay closes. */
  readonly closed = output<void>();

  /** Emits when user selects a month in year view. */
  readonly monthSelected = output<Date>();

  /** Emits when user selects a year in multi-year view. */
  readonly yearSelected = output<Date>();

  /** Emits true when the control has validation errors visible, false when errors are cleared. */
  readonly errorChange = output<boolean>();

  // =========================================================================
  // State Signals
  // =========================================================================

  /** Current selected date(s). Single mode: Date | null Range mode: DateRange */
  protected readonly selectedDate = signal<DateSelection>(null);

  /** Active date in calendar (focused/highlighted). Used for keyboard navigation. */
  protected readonly activeDate = signal<Date>(this.dateAdapter.today());

  /** Current calendar view. */
  protected readonly currentView = signal<CalendarView>(this.startView());

  // isOpen signal declared earlier (line 79)

  /** Parse error signal for tracking invalid input text. */
  protected readonly parseError = signal<{ text: string } | null>(null);

  /**
   * Raw text the user is currently typing. While this is set it wins over the formatted value, otherwise every
   * keystroke would be parsed, clamped and written straight back into the input.
   */
  private readonly typedText = signal<string | null>(null);

  /** Tracks whether the field has been touched (user interacted with it). */
  protected readonly touched = signal<boolean>(false);

  /** Tracks whether the field has been modified. */
  protected readonly dirty = signal<boolean>(false);

  // =========================================================================
  // Constructor
  // =========================================================================

  constructor() {
    super();

    // Trigger validation when checkboxEnabled changes
    effect(() => {
      // Read checkboxEnabled to track it
      this.checkboxEnabled();
      // Trigger validation update
      untracked(() => {
        this.onValidatorChange();
      });
    });

    // Emit errorChange whenever visible error state changes
    effect(() => {
      this.errorChange.emit(this._showErrors());
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();

    // Sync touched state from external FormControl so programmatic markAsTouched() is reflected
    const externalControl = this.ngControl?.control;
    if (externalControl) {
      externalControl.statusChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
        if (externalControl.touched && !this.touched()) {
          this.touched.set(true);
        }
      });
    }
  }

  // =========================================================================
  // Computed Signals
  // =========================================================================

  /**
   * Effective display format that uses the input displayFormat if provided, otherwise falls back to the injected
   * HX_DATE_FORMATS.display.
   */
  protected readonly effectiveDisplayFormat = computed(() => {
    return this.displayFormat() ?? this.formats.display;
  });

  /** Parse using the configured display format before trying adapter fallbacks. */
  protected readonly effectiveParseFormats = computed(() => {
    const displayFormat = this.effectiveDisplayFormat();
    return [displayFormat, ...this.formats.parse.filter((format) => format !== displayFormat)];
  });

  /**
   * Effective start view that automatically shows month/year picker when MM/YYYY format is selected. This provides a
   * better UX by skipping the day selection view when only month/year data is needed.
   *
   * @returns 'year' when displayFormat is 'MM/YYYY', otherwise returns the startView input value
   */
  protected readonly effectiveStartView = computed(() => {
    const format = this.effectiveDisplayFormat();
    // Auto-switch to year view (month picker) for MM/YYYY format
    if (format === 'MM/YYYY') {
      return 'year' as CalendarView;
    }
    return this.startView();
  });

  /** Formatted display value for input field. */
  protected readonly displayValue = computed(() => {
    const typed = this.typedText();
    if (typed !== null) {
      return typed;
    }

    const selected = this.selectedDate();
    if (!selected) {
      return '';
    }

    const format = this.effectiveDisplayFormat();
    const mode = this.mode();
    if (mode === 'single') {
      return this.dateAdapter.format(selected as Date, format);
    } else {
      const range = selected as DateRange;
      const startStr = range.start ? this.dateAdapter.format(range.start, format) : '';
      const endStr = range.end ? this.dateAdapter.format(range.end, format) : '';

      if (startStr && endStr) {
        return `${startStr} – ${endStr}`;
      } else if (startStr) {
        return `${startStr} – ...`;
      }
      return '';
    }
  });

  /**
   * Placeholder text based on mode. Custom placeholder (inherited from HxBaseControl) takes precedence over
   * format-based default.
   */
  protected readonly placeholderText = computed(() => {
    const custom = this.placeholder();
    if (custom) return custom;
    // Use the effective format pattern as placeholder hint
    const format = this.effectiveDisplayFormat();
    return this.mode() === 'range' ? `${format} – ${format}` : format;
  });

  /** Aria label text based on mode. */
  protected readonly ariaLabelText = computed(() => {
    return this.mode() === 'range' ? 'Select date range' : 'Select date';
  });

  /** True when field is marked as required and has no value, and has been touched. Used to show red border. */
  protected readonly isRequiredAndEmpty = computed(() => {
    const isEmpty = this.displayValue() === '';
    return (this.required() || this.checkboxEnabled()) && isEmpty && (this.touched() || this.dirty());
  });

  /** Determines if validation errors should be displayed based on form control state. */
  protected readonly _showErrors = computed(() => {
    const errors = this.validationErrors();
    if (!errors) return false;
    // Only show errors after user interaction (touched or dirty)
    return this.touched() || this.dirty();
  });

  /** True if range selection is complete (both start and end set). */
  protected readonly isRangeComplete = computed(() => {
    if (this.mode() !== 'range') {
      return true;
    }
    const range = this.selectedDate() as DateRange;
    return range?.start !== null && range?.end !== null;
  });

  /** Range start date (for calendar highlighting). */
  protected readonly rangeStart = computed(() => {
    if (this.mode() !== 'range') {
      return null;
    }
    const range = this.selectedDate() as DateRange;
    return range?.start ?? null;
  });

  /** Range end date (for calendar highlighting). */
  protected readonly rangeEnd = computed(() => {
    if (this.mode() !== 'range') {
      return null;
    }
    const range = this.selectedDate() as DateRange;
    return range?.end ?? null;
  });
  protected readonly validationErrors = computed<ValidationErrors | null>(() => {
    const date = this.selectedDate();
    // Effective bounds fold in restrictToPastOrToday, lookBackYears and friends, which typed dates must respect too.
    const min = this.effectiveMinDate();
    const max = this.effectiveMaxDate();
    const filter = this.effectiveDateFilter();
    const parseErr = this.parseError();
    const errors: ValidationErrors = {};

    // Required validation - also check if checkbox is enabled (making field conditionally required)
    if ((this.required() || this.checkboxEnabled()) && !date) {
      errors['required'] = true;
    }

    // Parse error takes precedence
    if (parseErr) {
      errors['matDatepickerParse'] = { text: parseErr.text };
    }

    if (this.mode() === 'single') {
      if (date) {
        // Min validation
        if (min && this.dateAdapter.compareDate(date as Date, min) < 0) {
          errors['matDatepickerMin'] = { min: min, actual: date };
        }
        // Max validation
        if (max && this.dateAdapter.compareDate(date as Date, max) > 0) {
          errors['matDatepickerMax'] = { max: max, actual: date };
        }
        // Filter validation
        if (filter && !filter(date as Date)) {
          errors['matDatepickerFilter'] = { value: date };
        }
      }
    } else if (this.mode() === 'range') {
      const range = date as DateRange;
      if (range?.start) {
        // Min validation for start
        if (min && this.dateAdapter.compareDate(range.start, min) < 0) {
          errors['matDatepickerMin'] = { min: min, actual: range.start };
        }
        // Max validation for start
        if (max && this.dateAdapter.compareDate(range.start, max) > 0) {
          errors['matDatepickerMax'] = { max: max, actual: range.start };
        }
      }
      if (range?.end) {
        // Min validation for end
        if (min && this.dateAdapter.compareDate(range.end, min) < 0) {
          errors['matDatepickerMin'] = { min: min, actual: range.end };
        }
        // Max validation for end
        if (max && this.dateAdapter.compareDate(range.end, max) > 0) {
          errors['matDatepickerMax'] = { max: max, actual: range.end };
        }
      }
      // Range validation (end >= start)
      if (range?.start && range?.end && this.dateAdapter.compareDate(range.end, range.start) < 0) {
        errors['dateRangeInvalid'] = { start: range.start, end: range.end };
      }
    }

    return Object.keys(errors).length > 0 ? errors : null;
  });

  /** Start date display value for range mode. */
  protected readonly startDateDisplayValue = computed(() => {
    if (this.mode() !== 'range') {
      return '';
    }
    const range = this.selectedDate() as DateRange;
    return range?.start ? this.dateAdapter.format(range.start, this.formats.display) : '';
  });

  /** End date display value for range mode. */
  protected readonly endDateDisplayValue = computed(() => {
    if (this.mode() !== 'range') {
      return '';
    }
    const range = this.selectedDate() as DateRange;
    return range?.end ? this.dateAdapter.format(range.end, this.formats.display) : '';
  });

  // =========================================================================
  // ViewChild References
  // =========================================================================

  private readonly dateInputRef = viewChild<HxInput>('dateInput');
  private readonly calendarButtonRef = viewChild<ElementRef<HTMLButtonElement>>('calendarButton');
  private openedViaButton = false;

  // =========================================================================
  // ControlValueAccessor Implementation
  // =========================================================================

  private onChange: (value: DateSelection) => void = () => {};
  private onTouched: () => void = () => {};

  override writeValue(value: unknown): void {
    this.typedText.set(null);
    const normalized = this.normalizeWrittenValue(value);
    this.selectedDate.set(normalized);
    if (normalized instanceof Date) {
      this.activeDate.set(normalized);
    } else if (normalized?.start) {
      this.activeDate.set(normalized.start);
    }
  }

  /** Converts raw values (e.g. ISO strings from a saved response) into the Date shapes the picker validates. */
  private normalizeWrittenValue(value: unknown): DateSelection {
    if (value && typeof value === 'object' && 'start' in value) {
      const range = value as { start?: unknown; end?: unknown };
      return { start: this.normalizeWrittenDate(range.start), end: this.normalizeWrittenDate(range.end) };
    }
    return this.normalizeWrittenDate(value);
  }

  private normalizeWrittenDate(value: unknown): Date | null {
    if (value instanceof Date) {
      return value;
    }
    if (typeof value !== 'string') {
      return null;
    }
    const date = this.dateAdapter.fromIso8601(value) ?? this.dateAdapter.parse(value, this.effectiveParseFormats());
    return date && this.dateAdapter.isValid(date) ? date : null;
  }

  override registerOnChange(fn: (value: DateSelection) => void): void {
    this.onChange = fn;
  }

  override registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  override setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled);
  }

  // =========================================================================
  // Validator Implementation
  // =========================================================================

  private onValidatorChange: () => void = () => {};

  override validate(): ValidationErrors | null {
    return this.validationErrors();
  }

  override registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  // =========================================================================
  // Public API
  // =========================================================================

  /**
   * Check if the form control has a specific validation error.
   *
   * @param errorCode - The error key to check (e.g., 'matDatepickerMin', 'matDatepickerParse')
   * @returns True if the error exists on the control
   */
  hasError(errorCode: string): boolean {
    const errors = this.validationErrors();
    return errors ? errorCode in errors : false;
  }

  /**
   * Get the error object for a specific validation error.
   *
   * @param errorCode - The error key to retrieve (e.g., 'matDatepickerMin', 'matDatepickerParse')
   * @returns The error object or undefined if not present
   *
   *   **Error Keys**:
   *
   *   - `matDatepickerParse`: Invalid date input text (e.g., { text: '2025-99-99' })
   *   - `matDatepickerMin`: Date below minimum boundary (e.g., { min: Date, actual: Date })
   *   - `matDatepickerMax`: Date above maximum boundary (e.g., { max: Date, actual: Date })
   *   - `matDatepickerFilter`: Date filtered out by custom filter (e.g., { value: Date })
   *   - `dateRangeInvalid`: End date before start date (e.g., { start: Date, end: Date })
   */
  getError(errorCode: string): any {
    const errors = this.validationErrors();
    return errors ? errors[errorCode] : undefined;
  }

  /**
   * Get a human-readable error message for a validation error type.
   *
   * @param errorType - The error type (e.g., 'required', 'matDatepickerMin')
   * @returns The error message string
   */
  protected _getErrorMessage(errorType: string): string {
    switch (errorType) {
      case 'required':
        return this.requiredErrorMessage() || 'This field is required';
      case 'matDatepickerMin':
        return 'Date is before the minimum allowed date';
      case 'matDatepickerMax':
        return 'Date is after the maximum allowed date';
      case 'matDatepickerFilter':
        return 'This date is not available for selection';
      case 'matDatepickerParse':
        return 'Invalid date format';
      case 'dateRangeInvalid':
        return 'End date must be after start date';
      default:
        return 'Invalid date';
    }
  }

  /** Programmatically open the calendar overlay. */
  open(): void {
    if (!this.disabledState() && !this.readonly() && !this.isOpen()) {
      const selected = this.selectedDate();
      if (selected instanceof Date) {
        this.activeDate.set(selected);
      } else if (selected && typeof selected === 'object' && 'start' in selected && selected.start) {
        this.activeDate.set(selected.start);
      }
      this.createOverlay();
      this.isOpen.set(true);
      this.opened.emit();
    }
  }

  /** Programmatically close the calendar overlay. */
  close(restoreFocus = true): void {
    if (this.isOpen()) {
      this.destroyOverlay();
      this.isOpen.set(false);
      this.onTouched();
      this.closed.emit();
      if (restoreFocus) {
        if (this.openedViaButton) {
          this.calendarButtonRef()?.nativeElement?.focus();
        } else {
          this.dateInputRef()?.focus();
        }
      }
      this.openedViaButton = false;
    }
  }

  /** Create and attach the calendar overlay. */
  private createOverlay(): void {
    if (this.overlayRef) {
      return; // Already created
    }

    const config = this.getOverlayConfig();
    this.overlayRef = this.overlay.create(config);

    // Create calendar component portal
    const calendarPortal = new ComponentPortal(HxCalendar);
    const calendarRef = this.overlayRef.attach(calendarPortal);
    this.calendarComponentRef = calendarRef;

    // Configure calendar component inputs
    calendarRef.setInput('activeDate', this.activeDate());

    // In single mode, pass selected date; in range mode, don't use 'selected'
    if (this.mode() === 'single') {
      calendarRef.setInput('selected', this.selectedDate());
      calendarRef.setInput('rangeStart', null);
      calendarRef.setInput('rangeEnd', null);
    } else {
      calendarRef.setInput('selected', null);
      calendarRef.setInput('rangeStart', this.rangeStart());
      calendarRef.setInput('rangeEnd', this.rangeEnd());
    }

    calendarRef.setInput('minDate', this.effectiveMinDate());
    calendarRef.setInput('maxDate', this.effectiveMaxDate());
    calendarRef.setInput('dateFilter', this.effectiveDateFilter());
    calendarRef.setInput('allowedYears', this.allowedYears());
    calendarRef.setInput('startView', this.effectiveStartView());
    calendarRef.setInput('finalizeOnMonthSelection', this.effectiveDisplayFormat() === 'MM/YYYY');
    calendarRef.setInput('dateAdapter', this.dateAdapter);

    // Handle calendar outputs
    calendarRef.instance.selectedChange.subscribe((date: Date) => {
      this.handleDateSelection(date);
    });

    calendarRef.instance.activeDateChange.subscribe((date: Date) => {
      this.activeDate.set(date);
      // Update the calendar's activeDate input when navigation occurs
      calendarRef.setInput('activeDate', date);
      calendarRef.changeDetectorRef.detectChanges();
      setTimeout(() => {
        if (this.isOpen() && this.calendarComponentRef) {
          this.calendarComponentRef.instance.focusActiveCell();
        }
      });
    });

    calendarRef.instance.monthSelected.subscribe((date: Date) => {
      this.monthSelected.emit(date);
    });

    calendarRef.instance.yearSelected.subscribe((date: Date) => {
      this.yearSelected.emit(date);
    });

    // Close on backdrop click (only in single mode or if range is complete)
    this.overlayRef.backdropClick().subscribe(() => {
      if (this.mode() === 'single') {
        this.close();
      } else {
        const range = this.selectedDate() as DateRange;
        // Only close if range is complete or not started
        if (!range || !range.start || range.end) {
          this.close();
        }
      }
    });

    // Close on Escape key anywhere within overlay
    this.overlayRef.keydownEvents().subscribe((event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      }
    });

    // Auto-focus active date cell in the calendar for immediate keyboard navigation
    calendarRef.changeDetectorRef.detectChanges();
    setTimeout(() => {
      if (this.isOpen() && this.calendarComponentRef) {
        this.calendarComponentRef.instance.focusActiveCell();
      }
    });
  }

  /** Destroy the calendar overlay. */
  private destroyOverlay(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = undefined;
      this.calendarComponentRef = undefined;
    }
  }

  /** Get overlay configuration. */
  private getOverlayConfig(): OverlayConfig {
    // Position relative to the input element, not the datepicker component
    const target = this._datepickerInput?._elementRef || this.elementRef;
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(target)
      .withPositions(this.getOverlayPositions())
      .withPush(false);

    const panelClasses = ['hx-date-picker-overlay'];
    const customClass = this.panelClass();
    if (customClass) {
      if (Array.isArray(customClass)) {
        panelClasses.push(...customClass);
      } else {
        panelClasses.push(customClass);
      }
    }

    return new OverlayConfig({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: this.backdropClass(),
      panelClass: panelClasses,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
    });
  }

  /** Get preferred overlay positions. */
  private getOverlayPositions(): ConnectedPosition[] {
    return [
      // Below start-aligned
      {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        offsetY: 2,
      },
      // Above start-aligned
      {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
        offsetY: -2,
      },
      // Below end-aligned
      {
        originX: 'end',
        originY: 'bottom',
        overlayX: 'end',
        overlayY: 'top',
        offsetY: 2,
      },
      // Above end-aligned
      {
        originX: 'end',
        originY: 'top',
        overlayX: 'end',
        overlayY: 'bottom',
        offsetY: -2,
      },
    ];
  }

  /** Handle date selection from calendar. */
  private handleDateSelection(date: Date): void {
    this.typedText.set(null);

    if (this.mode() === 'single') {
      this.selectedDate.set(date);
      this.activeDate.set(date);

      this.onChange(date);
      this.dateChange.emit(date);
      // Update the input element value
      if (this._datepickerInput) {
        this._datepickerInput.value = date;
      }
      // Update calendar to show selected state
      if (this.calendarComponentRef) {
        this.calendarComponentRef.setInput('selected', date);
      }
      // Close calendar after single date selection
      this.close();
    } else {
      // Range mode logic
      const currentRange = (this.selectedDate() as DateRange) || { start: null, end: null };

      if (!currentRange.start || (currentRange.start && currentRange.end)) {
        // Start new range - reset both start and end
        const newRange = { start: date, end: null };
        this.selectedDate.set(newRange);
        this.activeDate.set(date);

        // Don't emit onChange yet, wait for end date

        // Update calendar to show only range start (no end yet)
        if (this.calendarComponentRef) {
          this.calendarComponentRef.setInput('rangeStart', date);
          this.calendarComponentRef.setInput('rangeEnd', null);
          this.calendarComponentRef.setInput('selected', null);
        }
        // Keep calendar open for end date selection
      } else {
        // Complete range
        const start = currentRange.start;
        const end = date;

        // Check if range is invalid (end < start)
        if (this.dateAdapter.compareDate(end, start) < 0) {
          // Invalid range - don't set it, just keep the start date
          // Keep only the start date, clear the end
          const invalidRange: DateRange = { start, end: null };
          this.selectedDate.set(invalidRange);
          this.onChange(invalidRange);
          this.dateChange.emit(invalidRange);
          this.onValidatorChange();

          // Update calendar to show only start
          if (this.calendarComponentRef) {
            this.calendarComponentRef.setInput('rangeStart', start);
            this.calendarComponentRef.setInput('rangeEnd', null);
            this.calendarComponentRef.setInput('selected', null);
          }

          // Close the calendar since the selection is invalid
          this.close();
          return;
        }

        // Valid range - set it
        const finalRange: DateRange = { start, end };

        this.selectedDate.set(finalRange);
        this.activeDate.set(date);
        this.onChange(finalRange);
        this.dateChange.emit(finalRange);
        this.onValidatorChange();

        // Update calendar to show complete range
        if (this.calendarComponentRef) {
          this.calendarComponentRef.setInput('rangeStart', finalRange.start);
          this.calendarComponentRef.setInput('rangeEnd', finalRange.end);
          this.calendarComponentRef.setInput('selected', null);
        }
        // Close calendar after range selection is complete
        this.close();
      }
    }
  }

  /** Toggle calendar overlay open/closed. */
  protected toggleCalendar(): void {
    if (this.readonly()) return;
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  // =========================================================================
  // Input Event Handlers
  // =========================================================================

  /** Handle input value change from hx-input component. Supports both single and range modes. */
  protected onInputValueChange(event: InputChangeEvent): void {
    this.dirty.set(true);
    this.typedText.set(event.value);
    const value = event.value.trim();

    if (!value) {
      this.selectedDate.set(this.mode() === 'range' ? { start: null, end: null } : null);
      this.parseError.set(null);
      this.dateInput.emit(this.selectedDate());
      this.onValidatorChange();
      return;
    }

    if (this.mode() === 'single') {
      this.parseSingleDate(value);
    } else {
      this.parseDateRange(value);
    }

    this.dateInput.emit(this.selectedDate());
  }

  /** Parse a single date value from input text. Handles validation, min/max bounds, and custom filters. */
  private parseSingleDate(value: string): void {
    const parsedDate = this.dateAdapter.parse(value, this.effectiveParseFormats());
    if (parsedDate && this.dateAdapter.isValid(parsedDate) && !this.hasRolledOver(value, parsedDate)) {
      this.parseError.set(null);
      // Bounds and filter violations are reported through validationErrors rather than silently rewriting what the
      // user typed.
      this.selectedDate.set(parsedDate);
      this.activeDate.set(parsedDate);
      this.onValidatorChange();
    } else {
      // Set parse error
      this.parseError.set({ text: value });
      this.selectedDate.set(null);
      this.onValidatorChange();
    }
  }

  /**
   * The date adapter rolls out-of-range parts over instead of rejecting them, so 09/31/2026 silently becomes 10/01/2026
   * and 13/45/2026 becomes 02/14/2027. Only fully numeric day/month/year text can be checked this way.
   */
  private hasRolledOver(value: string, parsed: Date): boolean {
    const parts = /^\s*(\d{1,2})\D(\d{1,2})\D(\d{2,4})\s*$/.exec(value);
    if (!parts) {
      return false;
    }

    const dayFirst = this.effectiveDisplayFormat().toUpperCase().startsWith('DD');
    const day = Number(dayFirst ? parts[1] : parts[2]);
    const month = Number(dayFirst ? parts[2] : parts[1]);
    const year = Number(parts[3]);

    return (
      this.dateAdapter.getDate(parsed) !== day ||
      this.dateAdapter.getMonth(parsed) + 1 !== month ||
      (parts[3].length === 4 && this.dateAdapter.getYear(parsed) !== year)
    );
  }

  /**
   * Parse a date range value from input text. Accepts formats like "MM/DD/YYYY – MM/DD/YYYY" or "MM/DD/YYYY -
   * MM/DD/YYYY"
   */
  private parseDateRange(value: string): void {
    // Try to split by various separators (en dash, em dash, hyphen, or just dash)
    const separators = ['–', '—', '-', ' - ', ' – ', ' — '];
    let parts: string[] = [];
    let foundSeparator = false;

    for (const separator of separators) {
      if (value.includes(separator)) {
        parts = value.split(separator).map((p) => p.trim());
        foundSeparator = true;
        break;
      }
    }

    // If no separator found, try to parse as a single date for start
    if (!foundSeparator || parts.length < 2) {
      const parsedDate = this.dateAdapter.parse(value, this.effectiveParseFormats());
      if (parsedDate && this.dateAdapter.isValid(parsedDate) && !this.hasRolledOver(value, parsedDate)) {
        this.parseError.set(null);
        this.selectedDate.set({ start: parsedDate, end: null });
        this.activeDate.set(parsedDate);
        this.onValidatorChange();
      } else {
        this.parseError.set({ text: value });
        this.selectedDate.set({ start: null, end: null });
        this.onValidatorChange();
      }
      return;
    }

    // Parse both dates
    const startStr = parts[0].trim();
    const endStr = parts[1].trim();

    const startDate = startStr ? this.dateAdapter.parse(startStr, this.effectiveParseFormats()) : null;
    const endDate = endStr ? this.dateAdapter.parse(endStr, this.effectiveParseFormats()) : null;

    const startValid = !!startDate && this.dateAdapter.isValid(startDate) && !this.hasRolledOver(startStr, startDate);
    const endValid = !!endDate && this.dateAdapter.isValid(endDate) && !this.hasRolledOver(endStr, endDate);

    // Text was supplied for a side but could not be read as a date.
    if ((startStr && !startValid) || (endStr && !endValid)) {
      this.parseError.set({ text: value });
      this.selectedDate.set({ start: null, end: null });
      this.onValidatorChange();
      return;
    }

    if (!startValid && !endValid) {
      // Both dates invalid
      this.parseError.set({ text: value });
      this.selectedDate.set({ start: null, end: null });
      this.onValidatorChange();
      return;
    }

    this.parseError.set(null);

    // Bounds violations are reported through validationErrors rather than silently rewriting what the user typed.
    let validStart = startValid ? startDate : null;
    let validEnd = endValid ? endDate : null;

    // Ensure start <= end, swap if needed
    if (validStart && validEnd && this.dateAdapter.compareDate(validStart, validEnd) > 0) {
      [validStart, validEnd] = [validEnd, validStart];
    }

    this.selectedDate.set({ start: validStart, end: validEnd });
    if (validStart) {
      this.activeDate.set(validStart);
    } else if (validEnd) {
      this.activeDate.set(validEnd);
    }
    this.onValidatorChange();

    // Update calendar if open
    if (this.calendarComponentRef) {
      this.calendarComponentRef.setInput('rangeStart', validStart);
      this.calendarComponentRef.setInput('rangeEnd', validEnd);
    }
  }

  /** Handle input change (single mode). */
  protected onInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    if (!value) {
      this.selectedDate.set(null);
      this.parseError.set(null);
      this.dateInput.emit(null);
      this.onValidatorChange();
      return;
    }

    // Parse the input value
    const parsedDate = this.dateAdapter.parse(value, this.effectiveParseFormats());
    if (parsedDate && this.dateAdapter.isValid(parsedDate)) {
      // Clear parse error
      this.parseError.set(null);

      // Validate against min/max and filter
      let validDate = parsedDate;
      const min = this.min();
      const max = this.max();
      const filter = this.effectiveDateFilter();

      if (min && this.dateAdapter.compareDate(parsedDate, min) < 0) {
        validDate = min;
      }
      if (max && this.dateAdapter.compareDate(parsedDate, max) > 0) {
        validDate = max;
      }
      if (filter && !filter(parsedDate)) {
        // Date filtered out - set filter error
        this.selectedDate.set(null);
        this.dateInput.emit(null);
        this.onValidatorChange();
        return;
      }

      this.selectedDate.set(validDate);
      this.activeDate.set(validDate);
      this.onValidatorChange();
    } else {
      // Set parse error
      this.parseError.set({ text: value });
      this.selectedDate.set(null);
      this.onValidatorChange();
    }

    this.dateInput.emit(this.selectedDate());
  }

  /** Handle start date input change (range mode). */
  protected onStartDateInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    const currentRange = (this.selectedDate() as DateRange) || { start: null, end: null };

    if (!value) {
      this.selectedDate.set({ start: null, end: currentRange.end });
      this.parseError.set(null);
      this.dateInput.emit(this.selectedDate());
      this.onValidatorChange();
      return;
    }

    // Parse the input value
    const parsedDate = this.dateAdapter.parse(value, this.effectiveParseFormats());
    if (parsedDate && this.dateAdapter.isValid(parsedDate)) {
      // Clear parse error
      this.parseError.set(null);

      // Validate against min/max
      let validDate = parsedDate;
      const min = this.min();
      const max = this.max();

      if (min && this.dateAdapter.compareDate(parsedDate, min) < 0) {
        validDate = min;
      }
      if (max && this.dateAdapter.compareDate(parsedDate, max) > 0) {
        validDate = max;
      }

      // Check if new start would make invalid range
      let newEnd = currentRange.end;
      if (newEnd && this.dateAdapter.compareDate(validDate, newEnd) > 0) {
        // Invalid range - clear the end date
        newEnd = null;
      }

      this.selectedDate.set({ start: validDate, end: newEnd });
      this.activeDate.set(validDate);
      this.onValidatorChange();
    } else {
      // Set parse error
      this.parseError.set({ text: value });
      this.selectedDate.set({ start: null, end: currentRange.end });
      this.onValidatorChange();
    }

    this.dateInput.emit(this.selectedDate());
  }

  /** Handle end date input change (range mode). */
  protected onEndDateInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.trim();

    const currentRange = (this.selectedDate() as DateRange) || { start: null, end: null };

    if (!value) {
      this.selectedDate.set({ start: currentRange.start, end: null });
      this.parseError.set(null);
      this.dateInput.emit(this.selectedDate());
      this.onValidatorChange();
      return;
    }

    // Parse the input value
    const parsedDate = this.dateAdapter.parse(value, this.effectiveParseFormats());
    if (parsedDate && this.dateAdapter.isValid(parsedDate)) {
      // Clear parse error
      this.parseError.set(null);

      // Validate against min/max
      let validDate = parsedDate;
      const min = this.min();
      const max = this.max();

      if (min && this.dateAdapter.compareDate(parsedDate, min) < 0) {
        validDate = min;
      }
      if (max && this.dateAdapter.compareDate(parsedDate, max) > 0) {
        validDate = max;
      }

      // Check if new end would make invalid range
      const newStart = currentRange.start;
      if (newStart && this.dateAdapter.compareDate(validDate, newStart) < 0) {
        // Invalid range - don't set the end date
        this.selectedDate.set({ start: newStart, end: null });
        this.activeDate.set(validDate);
        this.onValidatorChange();
        this.dateInput.emit(this.selectedDate());
        return;
      }

      this.selectedDate.set({ start: newStart, end: validDate });
      this.activeDate.set(validDate);
      this.onValidatorChange();
    } else {
      // Set parse error
      this.parseError.set({ text: value });
      this.selectedDate.set({ start: currentRange.start, end: null });
      this.onValidatorChange();
    }

    this.dateInput.emit(this.selectedDate());
  }

  /** Handle input blur - commit changes. */
  protected onInputBlur(): void {
    if (!this.parseError()) {
      // Dropping the raw text re-renders the input from the parsed value, formatted and clamped.
      this.typedText.set(null);
    }

    this.touched.set(true);
    this.onTouched();
    this.onChange(this.selectedDate());
    this.dateChange.emit(this.selectedDate());
  }

  /** Commit the text field when it loses focus. */
  protected onInputFocusChange(event: InputFocusEvent): void {
    if (!event.focused) {
      this.onInputBlur();
    } else {
      this.onInputFocus();
    }
  }

  /** Close the calendar when focus moves outside the input, icon, and calendar overlay. */
  protected onFocusOut(event: FocusEvent): void {
    const relatedTarget = event.relatedTarget;
    const wrapper = event.currentTarget as HTMLElement;

    if (
      relatedTarget instanceof Node &&
      (wrapper.contains(relatedTarget) || this.overlayRef?.overlayElement.contains(relatedTarget))
    ) {
      return;
    }

    this.close(false);
  }

  /** Handle input focus (single mode). */
  protected onInputFocus(): void {
    // Focus handling will be enhanced in Phase 4
  }

  /** Handle start date focus (range mode). */
  protected onStartDateFocus(): void {
    // Focus handling will be enhanced in Phase 4
  }

  /** Handle end date focus (range mode). */
  protected onEndDateFocus(): void {
    // Focus handling will be enhanced in Phase 4
  }

  /** Handle calendar button click. */
  protected onButtonClick(): void {
    if (this.readonly() || this.disabledState()) {
      return;
    }
    this.openedViaButton = true;
    this.toggleCalendar();
  }

  /** Handle keyboard navigation in inputs. */
  protected onInputKeydown(event: KeyboardEvent): void {
    if (this.readonly() || this.disabledState()) {
      return;
    }

    if (event.key === 'Enter') {
      // Commit typed text on Enter, but do not open the calendar overlay
      this.onInputBlur();
    } else if (event.key === 'Escape' && this.isOpen()) {
      event.preventDefault();
      this.close();
    }
  }

  /** Handle keyboard events on the calendar toggle button. */
  protected onButtonKeydown(event: KeyboardEvent): void {
    if (this.readonly() || this.disabledState()) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.openedViaButton = true;
      this.toggleCalendar();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.openedViaButton = true;
      this.open();
    } else if (event.key === 'Escape' && this.isOpen()) {
      event.preventDefault();
      this.close();
    }
  }
}
